import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  BIENENSTOCK_MODULE_ID,
  FUEL_UNITS_PER_CANISTER,
  GENERATOR_MODULE_ID,
  GYM_MODULE_ID,
  INTEL_MODULE_ID,
  moduleLevel,
  RANGE_MODULE_ID,
  xpMultiplierFor,
  type GridState,
  type HideoutState,
} from "@rietz/shared";
import { uiActions } from "../actions.js";
import { useHud } from "../store.js";
import { formatEuros, formatMmSs } from "./format.js";
import {
  craftViews,
  firstFuelIid,
  generatorView,
  moduleViews,
  productionView,
  projectHideout,
  reasonLabel,
  stashCountsOf,
  trainingRows,
  type ActiveCraftView,
  type GeneratorView,
  type ModuleView,
  type NeedView,
  type RecipeView,
} from "./hideoutLogic.js";
import { IconDeploy } from "./icons.js";
import {
  Badge,
  Bar,
  Card,
  CardTitle,
  Divider,
  KV,
  ListRow,
  Note,
  Num,
  SplitPage,
  Title,
} from "./kit.js";

/**
 * The cellar under the Gasthaus, as a screen: the trader tab's master/detail
 * rhythm, except that it MOVES — builds, crafts and the hive are countdowns.
 * It ticks locally once a second and sends nothing: the tick re-runs
 * `projectHideout`, which re-runs the SERVER's own `advanceHideout` against a
 * projection of its clock, so every number and every button on screen is
 * derived from one state. `ModuleDetail` is exported so the walkable shelter's
 * station panel (`ui/hideout/hideoutHud.tsx`) draws the very same card.
 * Materials come from the AUTHORITATIVE profile, not the displayed stash — a
 * build is charged against the stash the server has.
 */

export interface HideoutTabProps {
  hideout: HideoutState;
  euros: number;
  /** The AUTHORITATIVE stash — `profile.stash`, not the store's copy. */
  stash: GridState;
  /** `performance.now()` when that profile landed, for the projection. */
  profileAtMs: number;
}

/** For the list rows, which ask about crafts but not about materials. */
const EMPTY_COUNTS: ReadonlyMap<string, number> = new Map();

export function HideoutTab(props: HideoutTabProps): ReactElement {
  const now = useTicker();
  const h = useMemo(
    () => projectHideout(props.hideout, props.profileAtMs, now),
    [props.hideout, props.profileAtMs, now],
  );
  const counts = useMemo(() => stashCountsOf(props.stash), [props.stash]);
  const views = moduleViews(h, props.euros, counts, h.lastAdvanceMs);

  const [moduleId, setModuleId] = useState(views[0]?.def.id ?? "");
  const selected = views.find((v) => v.def.id === moduleId) ?? views[0] ?? null;

  if (selected === null) return <Note>The cellar is empty.</Note>;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        width: "100%",
        maxWidth: 1000,
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* THE WAY IN, and it is deliberately the warm button — the same class the
          deploy button wears — because the cellar is now a place rather than a
          screen, and everything below is the map of it. Nothing on this tab is
          taken away by walking down there: the list and the cards are still the
          way to read the whole cellar at once, which a man standing in one room
          of it cannot do. */}
      <header style={{ display: "flex", alignItems: "center", gap: 14, flex: "0 0 auto" }}>
        <button
          type="button"
          className="metadeploy"
          onClick={() => {
            uiActions().enterHideout();
          }}
        >
          Enter the shelter
          <IconDeploy size={16} />
        </button>
        <Note>
          Walk the bunker: build at the stations, shoot at the range, train in the gym. Esc brings
          you back.
        </Note>
      </header>

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <SplitPage
          side={260}
          maxWidth={1000}
          aside={
            <>
              <CardTitle>THE CELLAR</CardTitle>
              {views.map((view) => (
                <ListRow
                  key={view.def.id}
                  selected={view.def.id === selected.def.id}
                  onClick={() => setModuleId(view.def.id)}
                  title={view.def.name}
                  subtitle={statusLine(h, view)}
                  trailing={`Lv ${view.level}/${view.maxLevel}`}
                />
              ))}
            </>
          }
        >
          <ModuleDetail
            h={h}
            euros={props.euros}
            counts={counts}
            stash={props.stash}
            moduleId={selected.def.id}
            nowMs={h.lastAdvanceMs}
          />
        </SplitPage>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export interface ModuleDetailProps {
  /** The PROJECTED hideout — `projectHideout`'s answer, not the raw profile's. */
  h: HideoutState;
  euros: number;
  /** `stashCountsOf(profile.stash)`, for the cost lists. */
  counts: ReadonlyMap<string, number>;
  /** The AUTHORITATIVE stash — the generator card looks for a canister in it. */
  stash: GridState;
  moduleId: string;
  /** The projected SERVER instant every countdown below is measured from —
   *  `h.lastAdvanceMs`, which the projection has already moved forward. */
  nowMs: number;
}

/**
 * ONE MODULE, whole: what it is, what the next rung costs, what the room does.
 * Drawn in the tab's detail column and in the shelter's station panel,
 * identically — so it carries its own cards and leans on nothing around it. It
 * asks `moduleViews` for the whole cellar and picks one out, since the station
 * panel has no list to hand a view down from. An unknown id is a sentence, not
 * a crash.
 */
export function ModuleDetail(props: ModuleDetailProps): ReactElement {
  const { h, moduleId } = props;
  const view =
    moduleViews(h, props.euros, props.counts, props.nowMs).find((v) => v.def.id === moduleId) ??
    null;
  if (view === null)
    return <Note>Nothing here.</Note>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <header style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <Title size="lg">{view.def.name}</Title>
          <Badge tone="neutral">
            Level {view.level} of {view.maxLevel}
          </Badge>
        </div>
        <Note style={{ maxWidth: 620 }}>{view.def.blurb}</Note>
      </header>
      <Divider />

      {/* One or the other, never both: while a rung is being built the
          requirements card would describe THAT rung, priced and greyed out,
          which reads as an offer to buy what is already paid for. */}
      {view.building === null ? (
        <BuildCard view={view} />
      ) : (
        <Card className="mm-card-nested">
          <CardTitle>
            Under construction{view.next === null ? "" : ` · level ${view.next.level}`}
          </CardTitle>
          <Num tone="accent" style={{ fontSize: 22 }}>
            {formatMmSs(view.building.secLeft * 1000)}
          </Num>
          <Note>
            {view.next === null
              ? "The work carries on while you are in a raid."
              : `${view.next.blurb} — the work carries on while you are in a raid.`}
          </Note>
        </Card>
      )}

      {moduleId === GENERATOR_MODULE_ID ? (
        <GeneratorCard view={generatorView(h)} stash={props.stash} />
      ) : null}

      {moduleId === BIENENSTOCK_MODULE_ID ? <HiveCard h={h} /> : null}

      <CraftCards h={h} counts={props.counts} moduleId={moduleId} />

      {/* The three rooms whose whole point is something other than a recipe.
          Each is drawn for its own module and for nothing else, so the order
          below is only the order they would appear in if one room were somehow
          two of them at once. */}
      {moduleId === RANGE_MODULE_ID ? <RangeCard h={h} /> : null}
      {moduleId === GYM_MODULE_ID ? <GymCard h={h} /> : null}
      {moduleId === INTEL_MODULE_ID ? <IntelCard h={h} /> : null}
    </div>
  );
}

/**
 * One tick a second while mounted — the resolution of every number here
 * (`m:ss`, whole minutes of fuel). Exported for the shelter's station panel, so
 * there is one answer to how often the cellar ticks.
 */
export function useTicker(): number {
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(performance.now());
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);
  return now;
}

/** The one line under a module's name in the list: whatever it is doing that
 *  the player would want to know without clicking. */
function statusLine(h: HideoutState, view: ModuleView): string {
  if (view.building !== null) return `Building — ${formatMmSs(view.building.secLeft * 1000)}`;

  // The stash counts are irrelevant to `active` — the only field read here —
  // so this asks with an empty map rather than threading them through nine rows.
  const craft = craftViews(h, EMPTY_COUNTS, view.def.id).active;
  if (craft !== null) {
    return craft.done ? "Ready to collect" : `Crafting — ${formatMmSs(craft.secLeft * 1000)}`;
  }

  const prod = productionView(h, view.def.id);
  if (prod !== null) {
    return prod.stored > 0 ? `${prod.stored}/${prod.cap} ready to harvest` : "Producing";
  }

  if (view.def.id === GENERATOR_MODULE_ID && view.level > 0) {
    const gen = generatorView(h);
    if (gen.running) return `Running — ~${Math.floor(gen.minutesLeft ?? 0)} min of fuel`;
    if (view.next === null) return "Switched off";
  }

  // The three rooms whose state is an EFFECT rather than a countdown or buffer;
  // each wins over the price of its next rung, as the running generator does.
  if (view.def.id === GYM_MODULE_ID && view.level > 0) {
    // READY IF EITHER BENCH IS — the row names the ROOM — so the countdown is
    // the
    // SOONER of the two clocks.
    let soonest = Number.POSITIVE_INFINITY;
    for (const row of trainingRows(h, null, h.lastAdvanceMs)) {
      if (row.cooldownLeftSec < soonest) soonest = row.cooldownLeftSec;
    }
    return soonest <= 0 ? "Ready to train" : `Resting — ${formatMmSs(soonest * 1000)}`;
  }

  if (view.def.id === INTEL_MODULE_ID && view.level > 0) {
    return `×${xpMultiplierFor(view.level).toFixed(2)} experience`;
  }

  if (view.def.id === RANGE_MODULE_ID && view.level > 0) return "Open";

  if (view.next === null) return "Fully upgraded";
  return `Next level — ${formatEuros(view.next.euros)}`;
}

// ---------------------------------------------------------------------------

function BuildCard(props: { view: ModuleView }): ReactElement | null {
  const { next } = props.view;
  if (next === null) {
    return (
      <Card className="mm-card-nested">
        <CardTitle>Fully upgraded</CardTitle>
        <Note>There is nothing left to build here.</Note>
      </Card>
    );
  }

  return (
    <Card className="mm-card-nested">
      <CardTitle>
        Level {next.level} · {Math.round(next.buildSec / 60)} min to build
      </CardTitle>
      <Note>{next.blurb}</Note>

      <KV label="Cost">
        <Num tone={next.enough ? "accent" : "bad"}>{formatEuros(next.euros)}</Num>
      </KV>
      {next.items.map((item) => (
        <NeedRow key={item.defId} need={item} />
      ))}
      {next.prereqs.map((req) => (
        <KV key={`${req.name}${req.level}`} label="Requires">
          <Num tone={req.ok ? "good" : "bad"}>
            {req.name} level {req.level}
          </Num>
        </KV>
      ))}

      <Action
        label="Build"
        problem={next.problem}
        onClick={() => uiActions().hideoutBuild(props.view.def.id)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------

function GeneratorCard(props: { view: GeneratorView; stash: GridState }): ReactElement {
  const { view } = props;
  const fuelIid = firstFuelIid(props.stash);
  // The two refusals this screen decides itself (there is no shared
  // `powerProblem`), said in the server's vocabulary. OFF is always allowed.
  const switchProblem =
    view.on ? null : view.level === 0 ? "level" : view.fuelUnits > 0 ? null : "fuel";

  return (
    <Card className="mm-card-nested">
      <CardTitle>Fuel tank</CardTitle>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <Num tone={view.fuelUnits > 0 ? "default" : "bad"} style={{ fontSize: 20 }}>
          {Math.floor(view.fuelUnits)}/{view.tankCap}
        </Num>
        <Note>
          {view.minutesLeft === null
            ? "no generator to burn it"
            : `~${Math.floor(view.minutesLeft)} min at level ${view.level}`}
        </Note>
      </div>
      <Bar fill01={view.tankCap > 0 ? view.fuelUnits / view.tankCap : 0} />
      <Note>
        {view.running
          ? "Running. Powered recipes are making progress."
          : view.on
            ? "Switched on, but the tank is dry."
            : "Switched off. Powered recipes are standing still."}
      </Note>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <Action
          label={view.on ? "Turn off" : "Turn on"}
          problem={switchProblem}
          onClick={() => uiActions().hideoutPower(!view.on)}
        />
        <Action
          label="Add fuel"
          problem={
            view.level === 0
              ? "level"
              : fuelIid === null
                ? "items"
                : // A canister goes in whole or not at all, so a tank with room
                  // for only part of one is a tank with no room — the same
                  // reading the server takes, and the reason fuel is never
                  // silently poured away.
                  view.fuelUnits + FUEL_UNITS_PER_CANISTER > view.tankCap
                  ? "full"
                  : null
          }
          onClick={() => {
            if (fuelIid !== null) uiActions().hideoutFuel(fuelIid);
          }}
        />
      </div>
    </Card>
  );
}

function HiveCard(props: { h: HideoutState }): ReactElement | null {
  const prod = productionView(props.h, BIENENSTOCK_MODULE_ID);
  if (prod === null) return null;

  return (
    <Card className="mm-card-nested">
      <CardTitle>The buffer</CardTitle>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <Num tone={prod.stored > 0 ? "good" : "default"} style={{ fontSize: 20 }}>
          {prod.stored}/{prod.cap}
        </Num>
        <Note>
          {prod.secToNext === null
            ? "full — nothing more is being made"
            : `next jar in ${formatMmSs(prod.secToNext * 1000)}`}
        </Note>
      </div>
      <Action
        label="Harvest"
        problem={prod.stored > 0 ? null : "empty"}
        onClick={() => uiActions().hideoutHarvest(BIENENSTOCK_MODULE_ID)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The three rooms that make nothing: a range, a gym and a radio room pay out in
// shots, skill points and xp rather than items, so each gets a small readout
// instead of a recipe card — and, for the two that are USED, where to go.
// ---------------------------------------------------------------------------

function RangeCard(props: { h: HideoutState }): ReactElement | null {
  const level = moduleLevel(props.h, RANGE_MODULE_ID);
  // Nothing to describe before the tunnel is dug out — the build card above is
  // already making the whole argument for digging it.
  if (level < 1) return null;

  return (
    <Card className="mm-card-nested">
      <CardTitle>The lane</CardTitle>
      <Title>
        {level >= 2 ? "Five plates and a 30-second drill" : "Three plates at 10, 20 and 30 m"}
      </Title>
      <Note>Practice ammunition is free. Walk into the tunnel and press F at the table.</Note>
    </Card>
  );
}

/**
 * The bench: where the two trainable skills stand, what a session is worth,
 * whether the bench is free. THE ONE CARD HERE THAT READS THE STORE: the skills
 * come from the PROGRESSION, and threading it through `ModuleDetail` for one
 * card would reach every signature and render test. `null` before the gym is
 * built.
 */
function GymCard(props: { h: HideoutState }): ReactElement | null {
  const progress = useHud((s) => s.profile?.progress ?? null);
  if (moduleLevel(props.h, GYM_MODULE_ID) < 1) return null;

  const rows = trainingRows(props.h, progress, props.h.lastAdvanceMs);

  return (
    <Card className="mm-card-nested">
      <CardTitle>The bench</CardTitle>
      {rows.map((row) => (
        <KV key={row.skill} label={row.label}>
          <span style={{ display: "inline-flex", gap: 12, whiteSpace: "nowrap" }}>
            {/* One decimal, because a session moves a skill by a fraction of a
                point and a whole number would sit still through three of them. */}
            <Num>{row.value.toFixed(1)}</Num>
            <Num tone="accent">+{row.gainPerSession.toFixed(1)} per session</Num>
            <Num tone={row.cooldownLeftSec > 0 ? "dim" : "good"}>
              {row.cooldownLeftSec > 0
                ? `Ready in ${formatMmSs(row.cooldownLeftSec * 1000)}`
                : "Ready"}
            </Num>
          </span>
        </KV>
      ))}
      <Note>Training happens at the bench in the shelter — walk up to it and press F.</Note>
    </Card>
  );
}

/**
 * The radio room: one number, no verb. Drawn at level 0 too —
 * `xpMultiplierFor(0)` is 1, and "no bonus yet" beside the first level's price
 * is the room's argument for itself.
 */
function IntelCard(props: { h: HideoutState }): ReactElement {
  const mult = xpMultiplierFor(moduleLevel(props.h, INTEL_MODULE_ID));

  return (
    <Card className="mm-card-nested">
      <CardTitle>On the air</CardTitle>
      {mult > 1 ? (
        <Num tone="accent" style={{ fontSize: 20 }}>
          Experience ×{mult.toFixed(2)}
        </Num>
      ) : (
        <Note style={{ fontSize: 15 }}>No bonus yet</Note>
      )}
      <Note>
        Every raid and every quest is paid through the radio room. There is nothing here to press.
      </Note>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function CraftCards(props: {
  h: HideoutState;
  counts: ReadonlyMap<string, number>;
  moduleId: string;
}): ReactElement | null {
  const view = craftViews(props.h, props.counts, props.moduleId);
  if (view.recipes.length === 0) return null;

  return (
    <>
      {view.active === null ? null : <ActiveCraftCard active={view.active} />}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 10,
          alignItems: "start",
        }}
      >
        {view.recipes.map((recipe) => (
          <RecipeCard key={recipe.def.id} recipe={recipe} />
        ))}
      </div>
    </>
  );
}

function ActiveCraftCard(props: { active: ActiveCraftView }): ReactElement {
  const { active } = props;
  return (
    <Card className="mm-card-nested">
      <CardTitle>
        {active.done ? "Finished" : "On the bench"} · {active.outputCount}× {active.outputName}
      </CardTitle>
      <Bar fill01={active.progress01} done={active.done} />
      <Note>
        {active.done
          ? "Waiting to be carried up to the stash."
          : `${formatMmSs(active.secLeft * 1000)} left${active.def.needsPower ? ", while the generator runs" : ""}`}
      </Note>
      <Action
        label="Collect"
        problem={active.done ? null : "not-done"}
        onClick={() => uiActions().hideoutCollect(active.def.id)}
      />
    </Card>
  );
}

function RecipeCard(props: { recipe: RecipeView }): ReactElement {
  const { recipe } = props;
  return (
    <Card tight className="mm-card-inset">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
          <Title>
            {recipe.outputCount}× {recipe.outputName}
          </Title>
        </span>
        {recipe.def.needsPower ? <Badge tone="warn">needs power</Badge> : null}
      </div>
      {recipe.inputs.map((input) => (
        <NeedRow key={input.defId} need={input} />
      ))}
      <KV label="Takes">
        <Num>{recipe.minutes} min</Num>
      </KV>
      <Action
        label="Start"
        problem={recipe.problem}
        onClick={() => uiActions().hideoutCraft(recipe.def.id)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The two pieces the cards share, both of them built from the menu's kit.
// ---------------------------------------------------------------------------

/** A material line: what it is, and how much of it is in the stash. */
function NeedRow(props: { need: NeedView }): ReactElement {
  const { need } = props;
  return (
    <KV label={need.name}>
      <Num tone={need.ok ? "good" : "bad"}>
        {need.have}/{need.need}
      </Num>
    </KV>
  );
}

/**
 * A button that can only be pressed when the shared verdict says so, and says
 * why — as a `title` AND a line of dim text, since six grey buttons for six
 * reasons are unreadable without it.
 */
function Action(props: {
  label: string;
  problem: string | null;
  onClick: () => void;
}): ReactElement {
  const why = props.problem === null ? null : reasonLabel(props.problem);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 2 }}>
      <button
        type="button"
        className="metabtn"
        disabled={props.problem !== null}
        title={why ?? props.label}
        onClick={props.onClick}
      >
        {props.label}
      </button>
      {why === null ? null : <Note style={{ fontSize: 11 }}>{why}</Note>}
    </div>
  );
}
